import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import { toast } from "../../../../components/Toast/toast";
import Button from "../../../../components/Button/Button";
import { AuthContext } from "../../../../context/AuthContext";
import { Section, Card, FieldGrid, Field } from "./accountParts";
import ProfileSettingsCard from "./ProfileSettingsCard";
import ProfileSettingsForm from "./ProfileSettingsForm";
import AccountSettingsCard from "./AccountSettingsCard";
import AccountSettingsForm from "./AccountSettingsForm";
import DeleteAccountModal from "./DeleteAccountModal";

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

const AccountPage = () => {
  const navigate = useNavigate();
  const { setUser } = useContext(AuthContext);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/account-settings");
      setData(res.data.data);
    } catch (err) {
      console.error("Failed to load account settings", err);
      setError(err.response?.data?.message || "Failed to load account settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (url, payload, successMessage) => {
    try {
      const res = await api.put(url, payload);
      setData((prev) => ({ ...prev, ...res.data.data }));
      toast.success(res.data.message || successMessage);
      return true;
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Could not save your changes."
      );
      return false;
    }
  };

  const handleProfileSave = async (form, logo = {}) => {
    let profile;

    try {
      const res = await api.put("/account-settings/profile", form);
      profile = res.data.data.profile;
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Could not save your changes."
      );
      return false;
    }

    try {
      if (logo.logoFile) {
        const body = new FormData();
        body.append("file", logo.logoFile);
        const res = await api.post("/account-settings/logo", body);
        profile = { ...profile, logo_url: res.data.data.logo_url };
      } else if (logo.removeLogo) {
        await api.delete("/account-settings/logo");
        profile = { ...profile, logo_url: null };
      }
    } catch (err) {
      setData((prev) => ({ ...prev, profile }));
      toast.error(
        `Your details were saved, but the logo could not be ${
          logo.removeLogo ? "removed" : "uploaded"
        }. ${err.response?.data?.message || "Please try again."}`
      );
      return false;
    }

    setData((prev) => ({ ...prev, profile }));
    toast.success("Profile settings updated.");
    return true;
  };

  const handleDeleteAccount = async (password) => {
    setDeleting(true);
    try {
      await api.delete("/account-settings", { data: { password } });
      setUser(null);
      navigate("/login");
      return null;
    } catch (err) {
      return (
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Could not delete the account."
      );
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="h-8 w-8 animate-spin rounded-full border border-gray-300 border-t-brand-600" />
      </div>
    );
  }

  if (error) return <p className="text-sm text-danger-600">{error}</p>;

  const canEdit = data?.can_edit;

  if (editing === "profile") {
    return (
      <ProfileSettingsForm
        profile={data?.profile}
        onSave={handleProfileSave}
        onCancel={() => setEditing(null)}
      />
    );
  }

  if (editing === "settings") {
    return (
      <AccountSettingsForm
        settings={data?.settings}
        currencies={data?.currency_options || []}
        onSave={(payload) =>
          save("/account-settings/preferences", payload, "Account settings updated.")
        }
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div>
      <div className="space-y-6">
        <Section title="Account" large>
          <Card>
            <FieldGrid>
              <Field label="Type" value={data?.account?.type} />
              <Field label="Name" value={data?.account?.name} />
              <Field
                label="Created at"
                value={formatDateTime(data?.account?.created_at)}
              />
            </FieldGrid>
          </Card>
        </Section>

        <ProfileSettingsCard
          profile={data?.profile}
          canEdit={canEdit}
          onEdit={() => setEditing("profile")}
        />

        <AccountSettingsCard
          settings={data?.settings}
          canEdit={canEdit}
          onEdit={() => setEditing("settings")}
        />

        {canEdit && (
          <Section title="Account Deletion">
            <Card className="border-danger-200">
              <p className="text-sm text-gray-700">
                Deleting this account permanently erases everything it owns —
                clients, vendors, projects, jobs, invoices, payments and every
                uploaded file. The data is removed from the server straight away
                and cannot be recovered afterwards.
              </p>
              <p className="mt-2 text-sm text-gray-700">
                Only the account owner can do this, and you will be asked for your
                password to confirm.
              </p>
              <div className="mt-4">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setDeleteOpen(true)}
                >
                  Delete account
                </Button>
              </div>
            </Card>
          </Section>
        )}
      </div>

      {deleteOpen && (
        <DeleteAccountModal
          submitting={deleting}
          onCancel={() => setDeleteOpen(false)}
          onConfirm={handleDeleteAccount}
        />
      )}
    </div>
  );
};

export default AccountPage;